<script setup lang="ts">
import { onMounted, ref, watch, watchEffect } from "vue";
import { useRoute } from "vue-router";
import { useAnalyticManager } from "@magnit/analytic-events";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import Cookie from "@magnit/core/src/utilities/cookie";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import {
  cookiePolicyRoute,
  cookiePolicyStorageKey,
} from "./cookie-policy.vars";

defineProps<{
  hide?: boolean;
}>();

const emit = defineEmits<{ visible: [boolean] }>();

const { send } = useAnalyticManager();
const route = useRoute();

const acceptCookie = ref<string>();
const isShow = ref<boolean>();

const onConfirm = () => {
  Cookie.set(cookiePolicyStorageKey, "1");
  acceptCookie.value = "1";
  isShow.value = false;
  send("CookiePolicy:Accept:Click");
};

const onClickMore = () => {
  send("CookiePolicy:Policy:Click");
};

watchEffect(() => {
  if (acceptCookie.value === "" && route.fullPath !== cookiePolicyRoute) {
    isShow.value = true;
    send("CookiePolicy:Popup:View");
  }
});

onMounted(() => {
  acceptCookie.value = Cookie.get(cookiePolicyStorageKey) || "";
});
watch(isShow, (next) => {
  emit("visible", Boolean(next));
});
</script>

<template>
  <Transition name="popup-unit-cookie-policy">
    <div v-if="isShow && !hide" class="popup-unit-cookie-policy">
      <div class="popup-unit-cookie-policy__content">
        <VText
          tag="div"
          :font="{ xs: 'caption-large-regular-low', m: 'title-small' }"
          color="invert-primary"
          class="popup-unit-cookie-policy__title"
        >
          Мы используем файлы cookie
        </VText>

        <VText
          tag="div"
          :font="{ xs: 'caption-small-regular-low', m: 'body-small-regular' }"
          color="invert-primary"
        >
          Так сайт работает стабильнее, а&nbsp;входить в&nbsp;профиль повторно
          не&nbsp;нужно.
          <a :href="cookiePolicyRoute" target="_blank" @click="onClickMore">
            Подробнее
          </a>
        </VText>
      </div>
      <VButton
        class="popup-unit-cookie-policy__button"
        theme="invert"
        :size="{ xs: 's', m: 'l' }"
        @click="onConfirm"
      >
        <VHidden tag="span" :hide="{ xs: false, m: true }">Закрыть</VHidden>
        <VHidden tag="span" :hide="{ xs: true, m: false }"
          >Хорошо, закрыть</VHidden
        >
      </VButton>
    </div>
  </Transition>
</template>

<style lang="postcss" scoped>
.popup-unit-cookie-policy {
  padding: var(--pl-unit-x2) var(--pl-unit-x2) var(--pl-unit-x2)
    var(--pl-unit-x3);
  background: var(--pl-surface-intense-default);
  position: fixed;
  display: flex;
  align-items: center;
  width: calc(100% - 24px);
  transform: translateX(50%);
  bottom: 16px;
  right: 50%;
  border-radius: var(--u-cookie-policy-border-radius);
  box-shadow: var(--pl-shadow-l);
  z-index: var(--pl-z-index-toast);
  gap: var(--pl-unit-x2);

  @media (--pl-viewport-from-m) {
    width: 340px;
    padding: var(--pl-unit-x3);
    flex-direction: column;
    gap: var(--pl-unit-x3);
  }

  @media (--pl-viewport-from-l) {
    right: var(--pl-unit-x6);
    bottom: var(--pl-unit-x6);
    transform: unset;
  }

  & .pl-text a {
    text-decoration: underline;
  }

  & .pl-text a:hover {
    text-decoration: none;
  }

  &__content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;

    @media (--pl-viewport-from-m) {
      gap: var(--pl-unit-x1);
    }
  }

  & &__button {
    flex-shrink: 0;

    @media (--pl-viewport-from-m) {
      width: 100%;
    }
  }
}

/* Transition */
.popup-unit-cookie-policy-enter-active {
  transition: all 0.3s ease-out;
}

.popup-unit-cookie-policy-leave-active {
  transition: all 0.3s ease-in;
}

.popup-unit-cookie-policy-enter-from,
.popup-unit-cookie-policy-leave-to {
  opacity: 0;
  transform: translateX(50%) translateY(100%);
}
</style>
